// Dashboard Effects
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, exhaustMap, catchError, withLatestFrom, switchMap } from 'rxjs/operators';
import { DeviceService, DeviceRow, DashboardResponse, RowsPayload } from '../../services/device.service';
import * as DashboardActions from './dashboard.actions';
import { selectCurrentPage, selectPageSize, selectAppliedQuery } from './dashboard.selectors';
import { TelemetryService } from '../../core/services/telemetry.service';

@Injectable()
export class DashboardEffects {
  private actions$ = inject(Actions);
  private deviceService = inject(DeviceService);
  private store = inject(Store);
  private telemetry = inject(TelemetryService);

  loadDeviceTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadDeviceTypes),
      switchMap(() =>
        this.deviceService.getDeviceTypes().pipe(
          map((types: any[]) => {
            const deviceTypeMap: Record<string, string> = {};
            for (const t of types) {
              const id = String(t.id ?? t.Id ?? '');
              const uid = t.uid || t.Uid || t.UID || t.name || '';
              if (id && uid) {
                deviceTypeMap[id] = uid;
              }
            }
            return DashboardActions.loadDeviceTypesSuccess({ deviceTypeMap });
          }),
          catchError((error) => {
            const msg = error?.error?.detail || error?.message || 'Failed to load device types';
            this.telemetry.trackException(error, { action: 'loadDeviceTypes', statusCode: error instanceof HttpErrorResponse ? String(error.status) : '' });
            return of(DashboardActions.loadDeviceTypesFailure({ error: msg }));
          })
        )
      )
    )
  );

  loadDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadDashboard),
      withLatestFrom(
        this.store.select(selectCurrentPage),
        this.store.select(selectPageSize),
        this.store.select(selectAppliedQuery)
      ),
      exhaustMap(([action, currentPage, pageSize, appliedQuery]) => {
        const page = action.page ?? currentPage;
        const size = action.pageSize ?? pageSize;
        const query = action.query ?? appliedQuery;

        return this.deviceService.getDashboard(page, size, query).pipe(
          map((data: DashboardResponse) => {
            const result = this.extractDashboardData(data);
            return DashboardActions.loadDashboardSuccess(result);
          }),
          catchError((error) => {
            const msg = error?.error?.detail || error?.message || 'Failed to load dashboard';
            this.telemetry.trackException(error, { action: 'loadDashboard', statusCode: error instanceof HttpErrorResponse ? String(error.status) : '' });
            return of(DashboardActions.loadDashboardFailure({ error: msg }));
          })
        );
      })
    )
  );

  refreshDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.refreshDashboard),
      withLatestFrom(
        this.store.select(selectCurrentPage),
        this.store.select(selectPageSize),
        this.store.select(selectAppliedQuery)
      ),
      exhaustMap(([, currentPage, pageSize, appliedQuery]) =>
        this.deviceService.getDashboard(currentPage, pageSize, appliedQuery).pipe(
          map((data: DashboardResponse) => {
            const result = this.extractDashboardData(data);
            return DashboardActions.refreshDashboardSuccess(result);
          }),
          catchError((error) => {
            const msg = error?.error?.detail || error?.message || 'Failed to refresh dashboard';
            this.telemetry.trackException(error, { action: 'refreshDashboard', statusCode: error instanceof HttpErrorResponse ? String(error.status) : '' });
            return of(DashboardActions.refreshDashboardFailure({ error: msg }));
          })
        )
      )
    )
  );

  // After successful load, update filter options
  updateFilterOptionsOnLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadDashboardSuccess, DashboardActions.refreshDashboardSuccess),
      map(({ rows }) => {
        const hubSet = new Set<string>();
        const envSet = new Set<string>();
        const typeSet = new Set<string>();

        for (const row of rows) {
          if (row.IoTHubName) hubSet.add(row.IoTHubName);
          if (row.environment) envSet.add(row.environment);
          if (row.deviceTypeName) typeSet.add(row.deviceTypeName);
        }

        return DashboardActions.updateFilterOptions({
          hubOptions: Array.from(hubSet).sort(),
          environmentOptions: Array.from(envSet).sort(),
          deviceTypeOptions: Array.from(typeSet).sort(),
        });
      })
    )
  );

  // Reload dashboard when page changes
  pageChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.setPage, DashboardActions.nextPage, DashboardActions.previousPage),
      map(() => DashboardActions.loadDashboard({}))
    )
  );

  // Reload dashboard when search is applied
  applySearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.applySearch, DashboardActions.clearSearch),
      map(() => DashboardActions.loadDashboard({ page: 1 }))
    )
  );

  private extractDashboardData(data: DashboardResponse): {
    rows: DeviceRow[];
    total: number;
    pageSize: number;
    page: number;
    hasMore: boolean;
    nextCursor: string | null;
  } {
    let rows: DeviceRow[] = [];
    let total = 0;
    let pageSize = 50;
    let page = 1;
    let hasMore = false;
    let nextCursor: string | null = null;

    if (Array.isArray(data)) {
      rows = data;
      total = data.length;
    } else if ('rows' in data) {
      const payload = data as RowsPayload;
      rows = payload.rows ?? [];
      total = payload.total ?? rows.length;
      pageSize = payload.pageSize ?? 50;
      page = payload.page ?? 1;
      hasMore = payload.hasMore ?? false;
      nextCursor = payload.nextCursor ?? null;
    } else if ('items' in data) {
      rows = data.items ?? [];
      total = data.total ?? rows.length;
      pageSize = data.pageSize ?? 50;
      page = data.page ?? 1;
      hasMore = data.hasMore ?? false;
      nextCursor = data.nextCursor ?? null;
    }

    return { rows, total, pageSize, page, hasMore, nextCursor };
  }
}
